import React from 'react';
import {
 DefaultButton, Icon, MessageBar, MessageBarType, Stack, Text,
} from '@fluentui/react';
import moment from 'moment';
import AttachmentList from '../../attachments/AttachmentList';
import {
  BillitDetailSection,
  BillitKvGrid,
  BillitPaymentLabel,
  BillitStatusLabel,
  billitColors,
  billitDetailGridStyle,
  billitSummaryBarStyle,
} from '../billitDetailUi';
import BillitInvoiceDocumentView from '../BillitInvoiceDocumentView';
import { BillitInvoice } from '../types';

type PdfState = {
  url: string | null;
  loading: boolean;
  error: string | null;
};

type Props = {
  invoiceData: BillitInvoice;
  pdf: PdfState;
  actionsBar?: React.ReactNode;
  topBanners?: React.ReactNode;
  purchaseOrderLinks: React.ReactNode;
  attachmentRefreshKey?: number;
  onAddRemark?: () => void;
  supplierInfo?: Record<string, unknown> | null;
  communicationAlert?: React.ReactNode;
};

const formatMoney = (value: number | null | undefined, currency?: string | null) => {
  if (value == null) return null;
  return `${Number(value).toFixed(2)} ${currency || 'EUR'}`;
};

const BillitInvoiceDetailLayout = ({
  invoiceData,
  pdf,
  actionsBar,
  topBanners,
  purchaseOrderLinks,
  attachmentRefreshKey,
  onAddRemark,
  supplierInfo,
  communicationAlert,
}: Props) => {
  const logs = invoiceData.billit_invoice_logs || [];
  const currency = invoiceData.currency || 'EUR';

  const gegevensItems = [
    { label: 'Factuurnummer', value: invoiceData.order_number || '—' },
    { label: 'Referentie', value: invoiceData.reference },
    { label: 'Betalingsref.', value: invoiceData.payment_reference },
    {
      label: 'Totaal incl.',
      value: formatMoney(invoiceData.total_incl, currency),
      highlight: true,
    },
    { label: 'Totaal excl.', value: formatMoney(invoiceData.total_excl, currency) },
    { label: 'BTW', value: formatMoney(invoiceData.total_vat, currency) },
    { label: 'Te betalen', value: formatMoney(invoiceData.to_pay, currency) },
    {
      label: 'Achterstallig',
      value: invoiceData.overdue
        ? invoiceData.days_overdue
          ? `Ja (${invoiceData.days_overdue} dagen)`
          : 'Ja'
        : null,
    },
    ...(supplierInfo
      ? [
          { label: 'Leverancier', value: (supplierInfo.Name as string) || null },
          { label: 'BTW nr.', value: (supplierInfo.VATNumber as string) || null },
          {
            label: 'Adres',
            value: [supplierInfo.Address, supplierInfo.ZipCode, supplierInfo.City]
              .filter(Boolean)
              .join(', ') || null,
          },
        ]
      : []),
  ];

  const employeeComments = logs.filter(l => !l.is_from_management);
  const managementComments = logs.filter(l => l.is_from_management);
  const isFinal =
    invoiceData.order_status === 'approved' || invoiceData.order_status === 'rejected';
  const lastEmployee = [...employeeComments].sort(
    (a, b) => new Date(b.date_created).getTime() - new Date(a.date_created).getTime(),
  )[0];
  const lastManagement = [...managementComments].sort(
    (a, b) => new Date(b.date_created).getTime() - new Date(a.date_created).getTime(),
  )[0];
  const defaultManagementAlert =
    !communicationAlert &&
    !isFinal &&
    lastEmployee &&
    (!lastManagement ||
      new Date(lastEmployee.date_created) > new Date(lastManagement.date_created)) ? (
        <MessageBar messageBarType={MessageBarType.info} styles={{ root: { marginBottom: 12 } }}>
          Medewerker heeft opmerkingen — reageer of wijzig de status.
        </MessageBar>
      ) : null;

  return (
    <Stack tokens={{ childrenGap: 16 }}>
      {topBanners}

      <div style={billitSummaryBarStyle}>
        <BillitStatusLabel status={invoiceData.order_status} />
        <BillitPaymentLabel invoice={invoiceData} />
        <Text style={{ fontWeight: 600, fontSize: 15 }}>
          {invoiceData.order_number || `#${invoiceData.billit_order_id}`}
        </Text>
        {invoiceData.order_date && (
          <Text style={{ fontSize: 12, color: '#605e5c' }}>
            {moment(invoiceData.order_date).format('DD/MM/YYYY')}
          </Text>
        )}
        {invoiceData.total_incl != null && (
          <Text style={{ fontWeight: 600, color: billitColors.purple, marginLeft: 'auto' }}>
            {formatMoney(invoiceData.total_incl, currency)}
          </Text>
        )}
        {invoiceData.assigned_employee && (
          <Text style={{ fontSize: 12, color: billitColors.purple }}>
            <Icon iconName='Contact' style={{ marginRight: 4, fontSize: 12 }} />
            {invoiceData.assigned_employee.first_name} {invoiceData.assigned_employee.last_name}
          </Text>
        )}
      </div>

      {actionsBar && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            padding: '8px 10px',
            background: '#fff',
            border: '1px solid #edebe9',
            borderRadius: 2,
          }}
        >
          {actionsBar}
        </div>
      )}

      <div style={billitDetailGridStyle}>
        <Stack tokens={{ childrenGap: 12 }}>
          <BillitDetailSection title='Bestelbonnen' defaultCollapsed={false}>
            {purchaseOrderLinks}
          </BillitDetailSection>

          <BillitDetailSection title='Factuur' defaultCollapsed={false}>
            <BillitInvoiceDocumentView
              pdfUrl={pdf.url}
              pdfLoading={pdf.loading}
              pdfError={pdf.error}
              ublXmlContent={invoiceData.ubl_xml_content}
              height={460}
              fileName={`factuur-${invoiceData.order_number || invoiceData.billit_order_id}.pdf`}
            />
          </BillitDetailSection>

          <BillitDetailSection
            title='Bijlagen'
            badge={
              invoiceData.billit_invoice_attachments?.length
                ? invoiceData.billit_invoice_attachments.length
                : undefined
            }
          >
            <AttachmentList
              title=''
              endpoint={`/billit/invoices/${invoiceData.billit_order_id}/attachments`}
              blobPrefix={`billit-invoices/${invoiceData.id}`}
              refreshKey={attachmentRefreshKey}
            />
          </BillitDetailSection>
        </Stack>

        <Stack tokens={{ childrenGap: 12 }}>
          {invoiceData.assigned_employee && (
            <BillitDetailSection title='Toegewezen aan' defaultCollapsed={false}>
              <Text style={{ fontWeight: 600 }}>
                {invoiceData.assigned_employee.first_name}{' '}
                {invoiceData.assigned_employee.last_name}
              </Text>
            </BillitDetailSection>
          )}

          <BillitDetailSection title='Gegevens' defaultCollapsed={false}>
            <BillitKvGrid items={gegevensItems} />
          </BillitDetailSection>

          <BillitDetailSection
            title='Communicatie'
            badge={logs.length || undefined}
            defaultCollapsed={logs.length === 0}
            action={
              onAddRemark ? (
                <DefaultButton
                  onClick={onAddRemark}
                  iconProps={{ iconName: 'CommentAdd' }}
                  styles={{ root: { minHeight: 28, height: 28 } }}
                >
                  Opmerking
                </DefaultButton>
              ) : undefined
            }
          >
            {communicationAlert || defaultManagementAlert}
            {logs.length === 0 ? (
              <Text style={{ color: '#605e5c', fontSize: 13 }}>Nog geen opmerkingen.</Text>
            ) : (
              <Stack tokens={{ childrenGap: 8 }}>
                {[...logs]
                  .sort(
                    (a, b) =>
                      new Date(a.date_created).getTime() - new Date(b.date_created).getTime(),
                  )
                  .map(log => {
                  const fromEmployee = !log.is_from_management;
                  return (
                    <div
                      key={log.id}
                      style={{
                        padding: '8px 10px',
                        borderRadius: 2,
                        borderLeft: `3px solid ${fromEmployee ? billitColors.employeeAccent : billitColors.adminAccent}`,
                        background: fromEmployee ? billitColors.selectedBg : billitColors.neutralBg,
                      }}
                    >
                      <Stack horizontal horizontalAlign='space-between'>
                        <Text style={{ fontSize: 12, fontWeight: 600 }}>
                          {log.created_by_employee
                            ? `${log.created_by_employee.first_name} ${log.created_by_employee.last_name}`
                            : 'Systeem'}
                          <span style={{ fontWeight: 400, color: '#605e5c', marginLeft: 6 }}>
                            ({fromEmployee ? 'Medewerker' : 'Administratie'})
                          </span>
                        </Text>
                        <Text style={{ fontSize: 11, color: '#605e5c' }}>
                          {moment(log.date_created).format('DD/MM/YY HH:mm')}
                        </Text>
                      </Stack>
                      <Text style={{ fontSize: 13, whiteSpace: 'pre-wrap', marginTop: 4 }}>
                        {log.remarks}
                      </Text>
                    </div>
                  );
                })}
              </Stack>
            )}
          </BillitDetailSection>
        </Stack>
      </div>
    </Stack>
  );
};

export default BillitInvoiceDetailLayout;
